import React, { Suspense, useCallback, useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { debounce } from 'lodash'
import {
  ButtonV2,
  LoadingLogo,
  AutoSaveFeedback,
  MainTabsV2,
} from '@labrav/react-components'
import { useDispatch, useSelector } from '../../../store'
import { FooterV2 } from '../../../common/components/FooterV2/FooterV2'
import { isEmpty } from 'lodash'
import { useStyles } from './TemplateCreationFormikWrapper.styles'
import { Canvas } from '../Canvas/Canvas'
import { TemplateDesignForm } from './TemplateDesignForm/TemplateDesignForm'
import { useNavigate, useParams } from 'react-router-dom'
import { AutoSaveTypes } from '@labrav/common'
import { autoSave, clearAutosaveStatus } from '@labrav/common'
import { autoSaveStoreFrontTimeout } from '../../../common/utils/constants'
import { tabsChangeComponents } from '../../../common/utils/hooks/tabsChangeComponents'
import { ListingCardCreationFormikWrapper } from '../ListingCardCreationFormikWrapper/ListingCardCreationFormikWrapper'
import { ListingCanvas } from '../ListingCanvas/ListingCanvas'
import { TemplateName } from './TemplateName/TemplateName'
import clsx from 'clsx'
import {
  getListingById,
  saveListingData,
} from '../../../modules/storefront/listingCardCreation/action'
import { ListingCardInCreation } from '../../../modules/storefront/listingCardCreation/reducer'
import ListingCardFormikValidationSchema from '../../../common/utils/ListingCardFormikValidationSchema'
import { EmbedCodeModal } from '../../FlyoutStorefrontV2/Storefront/EmbedCodeModal/EmbedCodeModal'
import { useAccountId } from '@labrav/common'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

export const TemplateCreationFormikWrapper: React.FC = () => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const accountId = useAccountId({ product: 'flyout' })
  const defaultTabs = [
    {
      key: 'template',
      label: 'Appearance',
      renderElement: (
        <div data-testid={'storefront-content'}>
          <TemplateDesignForm />
        </div>
      ),
    },
    {
      key: 'listing-card',
      label: 'Content',
      renderElement: (
        <div data-testid={'storefront-metrics-content'}>
          <ListingCardCreationFormikWrapper />
        </div>
      ),
    },
  ]
  const { activeTabIndex, onChange, activeKey } = tabsChangeComponents({
    tabs: defaultTabs,
  })

  const autoSaveStatus = useSelector<{ status: AutoSaveTypes; error?: string }>(
    state => state.autoSave?.templateCreation ?? { status: 'standBy' }
  )
  const listingCardCreationFormData = useSelector<ListingCardInCreation>(
    state => state.listingCardInCreation
  )

  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.CREATE_LISTING_CARD)
  )
  const { templateId, listingCardId } = useParams<{
    templateId?: string
    listingCardId?: string
  }>()
  const navigate = useNavigate()
  const currentTemplateId =
    templateId || listingCardCreationFormData.listingCardData.templateId
  const integrationId =
    listingCardId || listingCardCreationFormData.listingCardData.integrationId
  useEffect(() => {
    const nameField = document.querySelector(
      '[name="listingCardData.name"]'
    ) as HTMLInputElement | null
    if (nameField) {
      nameField.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(currentTemplateId && integrationId)) {
      window.history.pushState(
        {},
        '',
        `/flyout/storefront/builder/listing-card/${integrationId}`
      )
    }
  }, [currentTemplateId, integrationId])

  const [openlModal, setOpenModal] = useState(false)
  const handleCloseModal = () => {
    setOpenModal(false)
  }
  const handleOpenModal = () => {
    setOpenModal(true)
  }

  useEffect(() => {
    const initializeData = async () => {
      if (!isEmpty(listingCardId)) {
        await dispatch(getListingById(listingCardId || ''))
      }
    }
    initializeData()
  }, [listingCardId, templateId])

  useEffect(() => {
    return () => {
      dispatch(clearAutosaveStatus())
    }
  }, [])

  const debouncedAutoSave = useCallback(
    debounce((values: ListingCardInCreation) => {
      dispatch(
        autoSave('templateCreation', async () => {
          try {
            await dispatch(saveListingData(values))
            return null
          } catch (error) {
            return error
          }
        })
      )
    }, autoSaveStoreFrontTimeout),
    [dispatch]
  )

  const noOps = () => void 0
  const sendListingTemplateData = useCallback(
    async (listingData: ListingCardInCreation) => {
      const integrationID = await dispatch(saveListingData(listingData, true))
    },
    [dispatch]
  )
  if (isLoading) {
    return <LoadingLogo />
  }
  return (
    <>
      <div
        className={classes.templateContainer}
        data-testid="storefront-template-form"
      >
        {!isEmpty(listingCardCreationFormData) && (
          <Formik
            initialValues={listingCardCreationFormData}
            validationSchema={ListingCardFormikValidationSchema}
            // initialTouched={templateInCreationTouched}
            validateOnMount={true}
            onSubmit={noOps}
            enableReinitialize={true}
          >
            {formikProps => {
              const { values } = formikProps

              useEffect(() => {
                if (values.listingCardData.name && activeKey === 'template') {
                  debouncedAutoSave(values)
                }
              }, [values])

              const handleSaveClick = () => {
                if (!values.listingCardData.name) {
                  formikProps.setFieldTouched(
                    'listingCardData.name',
                    true,
                    false
                  )
                  const nameField = document.querySelector(
                    '[name="listingCardData.name"]'
                  ) as HTMLInputElement | null
                  if (nameField) {
                    nameField.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    })
                    nameField.focus()
                  }
                  return
                }
                sendListingTemplateData(formikProps.values)
              }
              const handleEmbedCodeButton = async () => {
                handleOpenModal()
                await handleSaveClick()
              }
              return (
                <Form
                  className={classes.formStyle}
                  data-testid="formik-Container-template-creation"
                >
                  <div className={classes.templateForm}>
                    <div className={classes.root}>
                      <div className={classes.wrapper}>
                        <TemplateName />
                      </div>

                      <div className={clsx(classes.tabContainer)}>
                        <div className={classes.tabsWrapper}>
                          <MainTabsV2
                            activeTabIndex={activeTabIndex}
                            onChange={onChange}
                            tabs={defaultTabs}
                            tabsType={'main'}
                          />
                        </div>
                      </div>
                      <div
                        className={
                          activeKey !== 'template' ? classes.content : ''
                        }
                      >
                        <Suspense fallback={<div />}>
                          {defaultTabs[activeTabIndex]?.renderElement}
                        </Suspense>
                      </div>
                    </div>
                  </div>
                  {activeKey === 'template' ? (
                    <Canvas />
                  ) : (
                    <ListingCanvas handleSaveClick={() => void 0} />
                  )}

                  <FooterV2
                    actions={
                      <div className={classes.footerWrapper}>
                        {activeKey === 'template' ? (
                          <div>
                            {!autoSaveStatus ||
                            autoSaveStatus.status === 'standBy' ? (
                              <></>
                            ) : (
                              <div className={classes.autosave}>
                                <AutoSaveFeedback
                                  status={autoSaveStatus.status}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div></div>
                        )}
                        <div className={classes.footerWrapperButtons}>
                          <ButtonV2
                            data-testid="save-button-template"
                            styleType="outlined"
                            onClick={handleSaveClick}
                            disabled={
                              autoSaveStatus?.status === 'loading'
                                ? true
                                : false
                            }
                          >
                            Save
                          </ButtonV2>
                          <ButtonV2
                            data-testid="embed-button-card"
                            styleType="primary"
                            onClick={handleEmbedCodeButton}
                            disabled={
                              isEmpty(
                                values.listingCardData.integrationId ||
                                  listingCardCreationFormData.listingCardData
                                    .integrationId
                              ) ||
                              values.listingCardData.product.some(
                                item => !item?.product_id
                              )
                            }
                          >
                            Get embed code snippet
                          </ButtonV2>
                        </div>
                      </div>
                    }
                    width={'calc(100vw - 27px)'}
                  />
                </Form>
              )
            }}
          </Formik>
        )}
      </div>
      <EmbedCodeModal
        integrationId={
          listingCardCreationFormData.listingCardData.integrationId as string
        }
        partnerId={accountId as string}
        open={openlModal}
        onClose={handleCloseModal}
      />
    </>
  )
}
